import React, { lazy, Suspense, useState, useContext } from 'react'
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Stack from '@mui/material/Stack';

import { cloudflareCDN } from './util';
import { useMattoState } from "../MattoState";
import { AuthContext } from "../auth/Auth";
import { usePermissions, firebaseHelper } from "ui";
import { useProjectSchemas } from "../global-state/useProjectSchemas";
import { useCollections } from "../global-state/useCollections";
import { useLoginState } from "../studioxr/auth/LoginState";
import { associateCollectionWithProject, duplicateCollectionScheme } from "../v2/apis/collectionsSchemeApi";

import { ExistingCardMoreOptions } from '../projects_v2/components/ExistingCardMoreOptions';
import { DeleteComponentModal } from '../projects_v2/components/DeleteComponentModal';
import { ShareComponentModal } from '../projects_v2/components/ShareComponentModal';
import { AssociatedProjectModal } from '../projects_v2/components/AssociatedProjectModal';
import { CardTypes } from 'ts-interfaces';

const textCss = {textAlign:'center',textTransform:'capitalize', wordBreak: 'break-word', paddingLeft: '5px', paddingRight: '5px'}
const textTruncate = {overflow: 'hidden',textOverflow: 'ellipsis',display: '-webkit-box',WebkitLineClamp: '2', lineClamp: '2',WebkitBoxOrient: 'vertical'}

const imageListCss = { width: "7vw", height: "7vw", borderRadius:'13px', margin: '4px', cursor:'pointer',  marginTop: '17px', overflow: 'hidden'}

// Lazy load ShareResourceModal
const ShareResourceModal = lazy(() => import("../projects_v2/share_resource").then(module => ({ default: module.ShareResourceModal })));

export const CollectionItemCover = (props) => {
	// console.log('CollectionItemCover render');
	const {currentUser} = useContext(AuthContext);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [showShareModal, setShowShareModal] = useState(false);
	const [showAssociatedProjectModal, setShowAssociatedProjectModal] = useState(false);
	const [selectedCollection, setSelectedCollection] = useState(null);
	
	const { permissions } = usePermissions(currentUser?.uid, firebaseHelper.db);
	const setSnackbarMessage = useMattoState((state) => state.setSnackbarMessage);

	const canEditOrShare = props.canEditOrShare !== undefined ? props.canEditOrShare : true;


	// Create collection object from props for consistency with DisplayCollectionSchemes
	const collection = {
		id: props.id,
		name: props.name,
		thumbnails: props.thumbnail || [
			{ src: props.src1 },
			{ src: props.src2 },
			{ src: props.src3 }
		],
		updatedAt: props.lastUpdatedAt || Date.now(),
		createdAt: props.createdAt || Date.now(),
		uid: currentUser?.uid,
		associatedProjectId: props.associatedProjectId, // This should be passed from parent if available
		products: props.products || [],
		userImages: props.userImages || [],
		userMaterials: props.userMaterials || [],
		...props.data // Include any additional data from props
	};

	const ShowThumbnail = () => {
		if (props.coverImage?.length > 4) {
			return  (    
				<ImageList cols={1} sx={imageListCss} onClick={props.onClick} >
					<ImageListItem
						key={Math.random()}
						cols={1}
						rows={1}
					>
						<img srcSet={`${cloudflareCDN(props.coverImage,'height=120,format=auto') }?fit=crop&auto=format`}  />
					</ImageListItem>
				</ImageList> )
		}
		else return <QuiltedImageList images={props}/>
	}

	const handleCopy = async (e) => {
		e.stopPropagation();
		e.preventDefault();
		try {
			setSnackbarMessage('Duplicating collection...');
			
			// Try to get the full collection object from LoginState first
			const loginStateCollections = useLoginState.getState().collections;
			const fullCollection = loginStateCollections.find(c => c.id === props.id) || collection;
			
			console.log('Duplicating with collection:', fullCollection); // Debug log
			const duplicatedCollection = await duplicateCollectionScheme(fullCollection);
			
			// Update the local login state to include the new collection
			await useLoginState.getState().addOrUpdateCollection(duplicatedCollection);
			
			// Call parent's onCopy if provided for additional handling
			if (props.onCopy) {
				props.onCopy(e, duplicatedCollection);
			}
			
			setSnackbarMessage('Collection duplicated successfully');
		} catch (error) {
			console.error('Error duplicating collection:', error);
			console.error('Collection object being used:', collection); // Debug log
			setSnackbarMessage('Failed to duplicate collection: ' + error.message);
		}
	};

	const openDeleteModal = (e) => {
		e.stopPropagation();
		setSelectedCollection(collection);
		setShowDeleteModal(true);
	};

	const handleDelete = async () => {
		try {
			setSnackbarMessage('Deleting collection...');
			
			// Use LoginState to actually delete the collection from database
			await useLoginState.getState().deleteCollection(selectedCollection, currentUser?.uid);
			
			// Call parent's deleteIconOnclick if provided for additional handling
			if (props.deleteIconOnclick) {
				props.deleteIconOnclick(null, selectedCollection);
			}
			
			setShowDeleteModal(false);
			setSelectedCollection(null);
			setSnackbarMessage('Collection deleted successfully');
		} catch (error) {
			console.error('Error deleting collection:', error);
			
			// Show specific error message based on the error type
			let errorMessage = 'Failed to delete collection';
			if (error.message) {
				if (error.message.includes('permission')) {
					errorMessage = 'You don\'t have permission to delete this collection';
				} else if (error.message.includes('not found')) {
					errorMessage = 'Collection not found or already deleted';
				} else {
					errorMessage = error.message;
				}
			}
			
			setSnackbarMessage(errorMessage);
			setShowDeleteModal(false);
			setSelectedCollection(null);
		}
	};

	const openShareModal = (e) => {
		e.stopPropagation();
		setSelectedCollection(collection);
		setShowShareModal(true);
	};

	const handleShareCollection = () => {
		if (selectedCollection) {
			navigator.clipboard.writeText(`${window.location.origin}/project/new?collectionId=${selectedCollection.id}`);
			setSnackbarMessage(`Collection URL copied to clipboard`);
		}
		
		// Call parent's onShare if provided
		if (props.onShare) {
			props.onShare(null, selectedCollection);
		}
	};

	const openAssociatedProjectModal = (e) => {
		e.stopPropagation();
		setSelectedCollection(collection);
		setShowAssociatedProjectModal(true);
	};

	const handleAssociatedProjectChange = async ({ newProjectId, oldProjectId, collection: targetCollection = null }) => {
		const collectionToUpdate = targetCollection || selectedCollection;
		if (!collectionToUpdate) return;
		
		try {
			await associateCollectionWithProject(collectionToUpdate.id, newProjectId, currentUser.uid, permissions);
			
			const updatedCollection = {
				...collectionToUpdate,
				associatedProjectId: newProjectId,
				updatedAt: Date.now()
			};
			
			// If moving from one project to another
			if (oldProjectId && oldProjectId !== newProjectId) {
				useProjectSchemas.getState().removeCollectionFromProjects(collectionToUpdate.id);
			}
			
			// Add to new project
			if (newProjectId) {
				useProjectSchemas.getState().updateCollectionInProjects(updatedCollection);
			}
			
			// Update collection store
			useCollections.getState().setCollections(
				useCollections.getState().collections.map(b => b.id === collectionToUpdate.id ? updatedCollection : b)
			);
			
			setShowAssociatedProjectModal(false);
			setSelectedCollection(null);
			setSnackbarMessage('Collection association updated successfully');
			
			// Call parent's onAssociatedProjectChange if provided
			if (props.onAssociatedProjectChange) {
				props.onAssociatedProjectChange(null, updatedCollection);
			}
		} catch (error) {
			console.error('Error updating collection association:', error);
			setSnackbarMessage(error.message || 'Failed to update collection association');
		}
	};

	const handleRemoveFromProject = (e) => {
		e.stopPropagation();
		setSnackbarMessage('Removing collection from project...');
		handleAssociatedProjectChange({
			newProjectId: null,
			oldProjectId: collection?.associatedProjectId || null,
			collection: collection
		});
		
		// Call parent's onRemoveFromProject if provided
		if (props.onRemoveFromProject) {
			props.onRemoveFromProject(e, collection);
		}
	};

	const handleMoreOptions = {
		onEdit: (e) => {
			e.stopPropagation();
			if (props.editIconOnClick) {
				props.editIconOnClick(e);
			}
		},
		onDelete: openDeleteModal,
		onCopy: handleCopy,
		onAssociatedProjectChange: openAssociatedProjectModal,
		onRemoveFromProject: handleRemoveFromProject,
		onShare: openShareModal
	}

	return (
		<>
		<Grid container spacing={0}  >
			<Grid item xs={12} style={{display:'flex', justifyContent:'center',position: 'relative'}} onMouseOver={props.onMouseOver} onMouseOut={props.onMouseOut}>
				<ShowThumbnail />
		   </Grid>
		 	<Grid container item xs={12} justifyContent="center" >     		 
				<Typography variant="caption" style={{...textCss,...textTruncate, fontSize: '12px', fontWeight: 600, lineHeight: '13px', marginTop: '4px', marginBottom: '6px'}}>{props.name}</Typography> 
		 	</Grid>
			{props.type == 'collections' &&
				<Grid container item xs={12} alignItems="center" >     		 
					<Grid item xs={6} justifyContent="left" >     		 
						<Typography variant="caption" style={{...textCss,...textTruncate, fontSize: '10px', fontWeight: 400, lineHeight: '11px'}}>{props.getNoOfItems()} items</Typography> 
					</Grid>
					<Grid item xs={6} justifyContent="right" > 
						{canEditOrShare ? (
							<Stack 
								direction="row" 
								spacing={0}
								sx={{ 
									justifyContent: 'flex-end',
									alignItems: 'center',
									paddingRight: '10px'
								}}
								onClick={(e) => e.stopPropagation()}
							>
								{props.isTeamsProUser && (
									<Suspense fallback={null}>
										<ShareResourceModal 
											title={props.name} 
											thumbnail={props.thumbnail || [
												{ src: props.src1 },
												{ src: props.src2 },
												{ src: props.src3 }
											]} 
											resourceID={props.id} 
											type={CardTypes.Collections}
											owner={props.uid}
										/>
									</Suspense>
								)}

								<ExistingCardMoreOptions 
									type={CardTypes.Collections}
									onEdit={handleMoreOptions.onEdit}
									onDelete={handleMoreOptions.onDelete} 
									onCopy={handleMoreOptions.onCopy} 
									onAssociatedProjectChange={handleMoreOptions.onAssociatedProjectChange} 
									onShare={handleMoreOptions.onShare}
									onRemoveFromProject={handleMoreOptions.onRemoveFromProject}
									cardSize="small"
									isTeamsProUser={props.isTeamsProUser}
									isProUser={props.isProUser}
								/>
							</Stack>
						) : (
							<Typography 
								variant="caption" 
								style={{
									...textCss,
									fontSize: '10px',
									fontWeight: 400,
									lineHeight: '21px',
									color: '#666',
									textAlign: 'right'
								}}
							>
								View Only
							</Typography>
						)}
					</Grid>
				</Grid>
			}
		</Grid>
		
		{/* Modals */}
		{showDeleteModal && (
			<DeleteComponentModal 
				open={showDeleteModal} 
				setOpen={setShowDeleteModal} 
				onDelete={handleDelete} 
			/>
		)}
		{showShareModal && (
			<ShareComponentModal 
				name={selectedCollection?.name} 
				open={showShareModal} 
				setOpen={setShowShareModal} 
				onShare={handleShareCollection} 
			/>
		)}
		{showAssociatedProjectModal && (
			<AssociatedProjectModal 
				open={showAssociatedProjectModal} 
				onClose={() => setShowAssociatedProjectModal(false)}  
				onSave={handleAssociatedProjectChange} 
				data={selectedCollection} 
				type={CardTypes.Collections} 
			/>
		)}
		</>
	)
}

function srcset(image, size, rows = 1, cols = 1) {
	if(!image) return null;

  // check if image is base64
  if (image.startsWith("data:image")) {
	return {
	  src: image,
	  srcSet: `${image} 2x`
	};
  }

  return {
    src: `${image}?w=${size * cols}&h=${size * rows}&fit=crop&auto=format`,
    srcSet: `${image}?w=${size * cols}&h=${
      size * rows
    }&fit=crop&auto=format&dpr=2 2x`
  };
}

export default function QuiltedImageList(props) {
	let itemData;
	const isFavoritesCollection = false;//props.images.name == 'favorites'
	if(isFavoritesCollection){
		 itemData = [
			{
			  img: props.images.src1,
			  rows: 2,
			  cols: 3
			},
			{
			  img:props.images.src2,
			  rows: 2,
			},
			{
			  img: props.images.src3,
			  rows: 2,
			}
		  ];
	}
	else {
		 itemData = [
			{
			img: props.images.src1,
			rows: 2,
			cols: 3
			},
			{
			img:props.images.src2,
			cols: 2
			},
			{
			img: props.images.src3,
			cols: 2
			}
		];
	}
  return (
    <ImageList
      sx={imageListCss}
      variant="quilted"
      cols={5}
	  gap={1}
	  onClick={props.images.onClick}
    >
      {itemData.map((item,index) => (
        <ImageListItem
          key={Math.random()}
          cols={item.cols || 1}
          rows={item.rows || 1}
        >
          <img
            {...srcset(item.img, 121, item.rows, item.cols)}
            alt={item.title}
            loading="lazy"
			// style={isFavoritesCollection && index==0 ? {borderRadius:'0px 13px 13px 0px'}: isFavoritesCollection && index==1 ?{borderRadius:'0px 6px 6px 0px'}:{}}
          />
        </ImageListItem>
      ))}
    </ImageList>
  );
}